/**
 * Roadmap Comment Service
 *
 * Domain layer for roadmap ("Ideas") item comments. Datasource-agnostic: all
 * Firestore access is delegated to firestoreService, so this service can be
 * swapped onto a different backend (e.g. cloud functions) without touching the
 * domain-level orchestration here.
 *
 * Mirrors the content CommentService sibling
 * (@/domain/services/comment/commentService), swapping the content data calls
 * for the roadmap ones and the content remappers for the roadmap remappers.
 *
 * Flutter parity:
 *   - lib/data/repository/roadmap_repository.dart:49-94
 *     (getNotificationComments, getComments, addComment, likeComment)
 *   - lib/data/data_sources/roadmap_data_source.dart:219-493
 *     (the underlying queries/transactions). The assembled write maps match
 *     Flutter exactly: comment map = comment.toJson() (:383),
 *     like map = like.toJson() (:463), notification map = info.toJson()
 *     (:379, :454).
 */

import type { PageCursor } from '@/data/types/json';
import { firestoreService } from '@/data/services/firestoreService';
import { RoadmapCommentRemapper } from '@/domain/remappers/roadmapComment/roadmapCommentRemapper';
import { RoadmapCommentLikeRemapper } from '@/domain/remappers/roadmapCommentLike/roadmapCommentLikeRemapper';
import { NotificationInfoRemapper } from '@/domain/remappers/notificationInfo/notificationInfoRemapper';
import type { RoadmapCommentEntity } from '@/domain/entities/roadmapComment/roadmapCommentEntity';
import type { NotificationInfoDto } from '@/data/dtos/info/notificationInfoDto';
import type { RoadmapCommentLikeDto } from '@/data/dtos/roadmapCommentLike/roadmapCommentLikeDto';

export interface RoadmapCommentsPage {
  comments: RoadmapCommentEntity[];
  hasMore: boolean;
  cursor?: PageCursor;
}

export class RoadmapCommentService {
  private static instance: RoadmapCommentService;

  public static getInstance(): RoadmapCommentService {
    if (!RoadmapCommentService.instance) {
      RoadmapCommentService.instance = new RoadmapCommentService();
    }
    return RoadmapCommentService.instance;
  }

  /**
   * Paginated fetch of a roadmap item's comments (root if parentId is null,
   * else the children/replies of that parent).
   */
  async fetchComments(
    roadmapItemId: string,
    parentId: string | null,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<RoadmapCommentsPage> {
    const page = await firestoreService.getRoadmapComments(
      roadmapItemId,
      parentId,
      limitCount,
      cursor,
    );
    return {
      comments: page.items.map(data => RoadmapCommentRemapper.toEntity(data)),
      hasMore: page.hasMore,
      cursor: page.cursor,
    };
  }

  /**
   * Fetch the roadmap comments referenced by a notification deep-link.
   * Used for the RoadmapCommentInfo deep-link (notification tap).
   */
  async fetchCommentsByIds(
    roadmapItemId: string,
    ids: string[],
  ): Promise<RoadmapCommentEntity[]> {
    const items = await firestoreService.getRoadmapNotificationComments(roadmapItemId, ids);
    return items.map(data => RoadmapCommentRemapper.toEntity(data));
  }

  /**
   * Add a roadmap comment. Remaps the domain entity to its raw write map and
   * delegates the transaction to the data layer.
   */
  async addComment(
    comment: RoadmapCommentEntity,
    notification: NotificationInfoDto | null,
    commentReplyPopularity: number,
  ): Promise<boolean> {
    return firestoreService.addRoadmapComment({
      commentId: comment.id,
      roadmapItemId: comment.roadmapItemId,
      parentId: comment.parentId ?? null,
      commentToReplyId: comment.commentToReply?.replyId ?? null,
      commentFirestoreMap: RoadmapCommentRemapper.toJson(RoadmapCommentRemapper.toDto(comment)),
      notification:
        notification && notification.usersInfo.peerUserId
          ? {
              peerUserId: notification.usersInfo.peerUserId,
              infoFirestoreMap: NotificationInfoRemapper.toJson(notification),
            }
          : null,
      commentReplyPopularity,
    });
  }

  /**
   * Like or unlike a roadmap comment.
   */
  async likeComment(
    like: RoadmapCommentLikeDto,
    roadmapItemId: string,
    notification: NotificationInfoDto | null,
    commentLikePopularity: number,
  ): Promise<boolean> {
    return firestoreService.likeRoadmapComment({
      commentId: like.commentId,
      userId: like.userId,
      parentCommentId: like.parentCommentId ?? null,
      liked: like.liked,
      likeFirestoreMap: RoadmapCommentLikeRemapper.toJson(like),
      notification:
        notification && notification.usersInfo.peerUserId
          ? {
              peerUserId: notification.usersInfo.peerUserId,
              infoFirestoreMap: NotificationInfoRemapper.toJson(notification),
            }
          : null,
      roadmapItemId,
      commentLikePopularity,
    });
  }
}

export const roadmapCommentService = RoadmapCommentService.getInstance();
